import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { NeonText } from '../effects';
import { useLanguage } from '@/i18n';
import styles from './WhatIDo.module.css';
import {
  SiReact,
  SiUnity,
  SiOpenai,
} from 'react-icons/si';
import { MdVrpano, MdOutlineArchitecture, MdPhoneIphone, MdOutlineGroups } from 'react-icons/md';
import { RiGamepadLine } from 'react-icons/ri';
import { TbCube3dSphere } from 'react-icons/tb';

const iconComponents: Record<string, React.ComponentType<{ className?: string }>> = {
  leadership: MdOutlineGroups,
  web: SiReact,
  '3d': TbCube3dSphere,
  vr: MdVrpano,
  ai: SiOpenai,
  game: RiGamepadLine,
  architecture: MdOutlineArchitecture,
  mobile: MdPhoneIphone,
};

export function WhatIDo() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { t } = useLanguage();

  const services = [
    {
      id: 'cto-leadership',
      title: t.whatIDo.services.ctoLeadership.title,
      description: t.whatIDo.services.ctoLeadership.description,
      icon: 'leadership',
    },
    {
      id: 'fullstack-web',
      title: t.whatIDo.services.fullstack.title,
      description: t.whatIDo.services.fullstack.description,
      icon: 'web',
    },
    {
      id: '3d-configurators',
      title: t.whatIDo.services.configurators3d.title,
      description: t.whatIDo.services.configurators3d.description,
      icon: '3d',
    },
    {
      id: 'vr-ar-dev',
      title: t.whatIDo.services.xrDevelopment.title,
      description: t.whatIDo.services.xrDevelopment.description,
      icon: 'vr',
    },
    {
      id: 'ai-integration',
      title: t.whatIDo.services.aiIntegration.title,
      description: t.whatIDo.services.aiIntegration.description,
      icon: 'ai',
    },
    {
      id: 'serious-games',
      title: t.whatIDo.services.seriousGames.title,
      description: t.whatIDo.services.seriousGames.description,
      icon: 'game',
    },
    {
      id: 'architecture',
      title: t.whatIDo.services.architecture.title,
      description: t.whatIDo.services.architecture.description,
      icon: 'architecture',
    },
    {
      id: 'mobile-dev',
      title: t.whatIDo.services.mobile.title,
      description: t.whatIDo.services.mobile.description,
      icon: 'mobile',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <section id="what-i-do" className={styles.whatIDo} ref={ref}>
      <div className={styles.container}>
        <motion.div
          className={styles.header}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <NeonText as="h2" color="cyan" effect="glow" className={styles.title} animate={false}>
            {t.whatIDo.title}
          </NeonText>
          <p className={styles.subtitle}>
            {t.whatIDo.subtitle}
          </p>
        </motion.div>

        <motion.div
          className={styles.servicesGrid}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          {services.map((service) => {
            const IconComponent = iconComponents[service.icon] || SiUnity;
            return (
              <motion.div key={service.id} className={styles.serviceCard} variants={itemVariants}>
                <div className={styles.iconWrapper}>
                  <IconComponent className={styles.icon} />
                </div>
                <h3 className={styles.serviceTitle}>{service.title}</h3>
                <p className={styles.serviceDescription}>{service.description}</p>
                <div className={styles.cardGlow} />
              </motion.div>
            );
          })}
        </motion.div>

        {/* Scrolling text banner */}
        <div className={styles.scrollingBanner}>
          <div className={styles.scrollingText}>
            <span>{t.whatIDo.scrollingBanner.appDev}</span>
            <span className={styles.divider}>•</span>
            <span>VR/AR</span>
            <span className={styles.divider}>•</span>
            <span>Serious Games</span>
            <span className={styles.divider}>•</span>
            <span>iOS/Android</span>
            <span className={styles.divider}>•</span>
            <span>{t.whatIDo.scrollingBanner.creativeDesign}</span>
            <span className={styles.divider}>•</span>
            <span>{t.whatIDo.scrollingBanner.softwareArch}</span>
            <span className={styles.divider}>•</span>
            <span>{t.whatIDo.scrollingBanner.databases}</span>
            <span className={styles.divider}>•</span>
            <span>{t.whatIDo.scrollingBanner.videogames}</span>
            <span className={styles.divider}>•</span>
            <span>{t.whatIDo.scrollingBanner.appDev}</span>
            <span className={styles.divider}>•</span>
            <span>VR/AR</span>
            <span className={styles.divider}>•</span>
            <span>Serious Games</span>
            <span className={styles.divider}>•</span>
            <span>iOS/Android</span>
            <span className={styles.divider}>•</span>
          </div>
        </div>
      </div>
    </section>
  );
}
